import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { BusinessSelector } from '@/features/business-context';

// Desktop Navigation Link Component
interface NavLinkProps {
  to: string;
  children: React.ReactNode;
}

function NavLink({ to, children }: NavLinkProps) {
  const isActive = window.location.pathname === to;

  return (
    <Link
      to={to}
      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? 'bg-primary-50 text-primary-700'
          : 'text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900'
      }`}
    >
      {children}
    </Link>
  );
}

// Mobile Navigation Link Component
interface MobileNavLinkProps {
  to: string;
  children: React.ReactNode;
  onClick: () => void;
}

function MobileNavLink({ to, children, onClick }: MobileNavLinkProps) {
  const isActive = window.location.pathname === to;

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`block px-4 py-2.5 text-base font-medium transition-colors ${
        isActive
          ? 'bg-primary-50 text-primary-700'
          : 'text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900'
      }`}
    >
      {children}
    </Link>
  );
}

// Main TopNav Component
export function TopNav() {
  const { profile, signOut } = useAuth();
  const isSuperAdmin = profile?.role === 'super_admin';
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Close profile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
  setIsSigningOut(true);
  // signOut handles navigation internally via window.location.href
  await signOut();
  // Note: The code below this line won't execute because signOut 
  // navigates away, but we keep it as a fallback
  setIsSigningOut(false);
};

  // Get user initials for avatar
  const getInitials = (): string => {
    if (profile?.full_name) {
      return profile.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return 'U';
  };

  // Get display name
  const getDisplayName = (): string => {
    return profile?.full_name || 'User';
  };

  // Get role display text
  const getRoleDisplay = (): string => {
    if (!profile?.role) return '';
    const roleMap: Record<string, string> = {
      owner: 'Owner',
      staff: 'Staff',
      super_admin: 'Super Admin',
    };
    return roleMap[profile.role] || profile.role;
  };

  return (
    <nav className="bg-white border-b border-secondary-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Left side - Logo and Navigation */}
          <div className="flex items-center">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary-600 rounded-lg flex items-center justify-center shadow-sm">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <span className="text-lg font-semibold text-secondary-900 hidden sm:block">
                NailedIt Renos
              </span>
            </Link>

            {/* Desktop Navigation Links */}
<div className="hidden md:flex items-center ml-8 space-x-1">
  <NavLink to="/dashboard">Dashboard</NavLink>
  <NavLink to="/projects">Projects</NavLink>
  <NavLink to="/trips">Trips</NavLink>
  <NavLink to="/customers">Customers</NavLink>
  <NavLink to="/suppliers">Suppliers</NavLink>
  {/* <NavLink to="/invoices">Invoices</NavLink> */}
</div>
          </div>

          {/* Right side - Profile and Actions */}
          <div className="flex items-center gap-2">
            {/* Business Selector for Super Admins OR Business Name for regular users */}
{isSuperAdmin ? (
  <div className="hidden sm:block">
    <BusinessSelector />
  </div>
) : (
  profile?.business_name && (
    <span className="hidden lg:block text-sm text-secondary-500 mr-2">
      {profile.business_name}
    </span>
  )
)}

            {/* Profile Dropdown */}
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-secondary-100 
                           transition-colors focus:outline-none focus:ring-2 
                           focus:ring-primary-500 focus:ring-offset-2"
                aria-expanded={isProfileMenuOpen}
                aria-haspopup="true"
              >
                {/* Avatar */}
                <div
                  className="w-8 h-8 bg-primary-100 text-primary-700 rounded-full 
                              flex items-center justify-center text-sm font-medium"
                >
                  {getInitials()}
                </div>
                {/* Name (hidden on mobile) */}
                <span className="hidden sm:block text-sm font-medium text-secondary-700 max-w-32 truncate">
                  {getDisplayName()}
                </span>
                {/* Dropdown arrow */}
                <svg
                  className={`w-4 h-4 text-secondary-500 transition-transform ${
                    isProfileMenuOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isProfileMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg 
                             border border-secondary-200 py-1 z-50"
                  role="menu"
                >
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-secondary-100">
                    <p className="text-sm font-medium text-secondary-900 truncate">
                      {getDisplayName()}
                    </p>
                    {getRoleDisplay() && (
                      <p className="text-xs text-secondary-500 mt-0.5">
                        {getRoleDisplay()}
                      </p>
                    )}
                    {profile?.business_name && (
                      <p className="text-xs text-secondary-500 mt-0.5 truncate">
                        {profile.business_name}
                      </p>
                    )}
                  </div>

                  {/* Menu Items */}
                  {/* <DropdownMenuItem to="/settings" icon="settings">
                    Settings
                  </DropdownMenuItem> */}

                  {/* Sign Out */}
                  <div className="py-1">
                    <button
                      onClick={handleSignOut}
                      disabled={isSigningOut}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 
                                 hover:bg-red-50 transition-colors disabled:opacity-50"
                      role="menuitem"
                    >
                      {isSigningOut ? (
                        <>
                          <svg
                            className="animate-spin w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Signing out...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                          Sign out
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-secondary-500 hover:bg-secondary-100 
                         transition-colors focus:outline-none focus:ring-2 
                         focus:ring-primary-500"
              aria-expanded={isMobileMenuOpen}
              aria-label="Toggle navigation menu"
            >
              {isMobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
{isMobileMenuOpen && (
  <div className="md:hidden border-t border-secondary-200 py-2">
    {/* Business Selector for Super Admin on Mobile */}
    {isSuperAdmin && (
      <div className="px-4 py-3 border-b border-secondary-200 sm:hidden">
        <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide mb-2">
          Viewing as
        </p>
        <BusinessSelector />
      </div>
    )}
    <MobileNavLink to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}>
      Dashboard
    </MobileNavLink>
    <MobileNavLink to="/projects" onClick={() => setIsMobileMenuOpen(false)}>
      Projects
    </MobileNavLink>
    <MobileNavLink to="/trips" onClick={() => setIsMobileMenuOpen(false)}>
      Trips
    </MobileNavLink>
    <MobileNavLink to="/customers" onClick={() => setIsMobileMenuOpen(false)}>
      Customers
    </MobileNavLink>
    <MobileNavLink to="/suppliers" onClick={() => setIsMobileMenuOpen(false)}>
      Suppliers
    </MobileNavLink>
  </div>
)}
      </div>
    </nav>
  );
}